import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { cn } from "@/lib/utils";

/**
 * ScoreBreakdown — the ZenScore 7-signal composition visual.
 *
 * Renders each of the 7 subcategories (ADB+/NSR+/DC+/RH+/DL+/CL+/FS+) with
 * code, full name, description, and a proportionally-sized emerald bar that
 * visually communicates the relative weight of the signal — WITHOUT exposing
 * the numeric point weight (proprietary).
 *
 * The underlying `weight` values are relative-only and are never rendered as
 * text; they exist purely to size the visual bars.
 */
const SIGNALS = [
  {
    code: "ADB+",
    name: "Average Daily Balance",
    body: "Measures the sustained cash position of the business over time.",
    weight: 0.22,
  },
  {
    code: "NSR+",
    name: "Net Savings Ratio",
    body: "Reflects how much cash is retained after operating expenses.",
    weight: 0.18,
  },
  {
    code: "DC+",
    name: "Deposit Consistency",
    body: "Evaluates the regularity and predictability of business deposits.",
    weight: 0.13,
  },
  {
    code: "RH+",
    name: "Repayment History",
    body: "Tracks on-time payment behavior across obligations and prior funding.",
    weight: 0.20,
  },
  {
    code: "DL+",
    name: "Debt Load",
    body: "Assesses outstanding debt relative to business revenue.",
    weight: 0.08,
  },
  {
    code: "CL+",
    name: "Completed Loans",
    body: "Rewards businesses with a track record of fully repaid loans.",
    weight: 0.04,
  },
  {
    code: "FS+",
    name: "Financial Stress",
    body: "Detects signals of financial pressure (e.g., NSFs, sustained overdrafts).",
    weight: 0.15,
  },
];

const MAX_WEIGHT = Math.max(...SIGNALS.map((s) => s.weight));

function SignalRow({ signal, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  // Bars are sized relative to the largest weight so the biggest signal reaches
  // ~100% width — visually communicates rank without exposing the raw weights.
  const pct = (signal.weight / MAX_WEIGHT) * 100;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 12 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
      transition={{ duration: 0.45, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="grid grid-cols-[auto,1fr] items-center gap-4 rounded-2xl border border-white/[0.06] bg-bg-card px-5 py-5 sm:gap-6 sm:px-6"
      data-testid={`zenscore-signal-row-${signal.code.replace("+", "").toLowerCase()}`}
    >
      {/* Left — short code */}
      <div className="flex flex-col items-start">
        <span className="font-mono text-2xl font-bold leading-none tracking-tight text-brand-emerald sm:text-3xl">
          {signal.code}
        </span>
        <span className="mt-1 text-[9px] uppercase tracking-widest text-text-muted">
          Signal {String(index + 1).padStart(2, "0")}
        </span>
      </div>

      {/* Right — name + description + relative-weight bar */}
      <div className="min-w-0">
        <h4 className="font-display text-base font-semibold text-white sm:text-lg">
          {signal.name}
        </h4>
        <p className="mt-1 text-xs text-text-muted sm:text-sm">
          {signal.body}
        </p>
        <div
          className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/[0.05]"
          aria-hidden="true"
        >
          <motion.div
            initial={{ width: 0 }}
            animate={inView ? { width: `${pct}%` } : { width: 0 }}
            transition={{
              duration: 1.2,
              delay: index * 0.08 + 0.15,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="h-full rounded-full"
            style={{
              background: "linear-gradient(90deg, #00C853 0%, #00E676 100%)",
              boxShadow: "0 0 10px rgba(0,230,118,0.35)",
            }}
          />
        </div>
      </div>
    </motion.div>
  );
}

export const ScoreBreakdown = ({ className }) => {
  return (
    <div
      className={cn("flex flex-col gap-3", className)}
      data-testid="zenscore-score-breakdown"
    >
      {SIGNALS.map((s, i) => (
        <SignalRow key={s.code} signal={s} index={i} />
      ))}

      <p className="mt-6 text-center text-xs italic text-text-muted">
        Signal weightings are directional and evolve continuously as the
        underwriting model improves.
      </p>
    </div>
  );
};

export default ScoreBreakdown;
